import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	MesfModal,
	TimeService,
} from "@dexteel/mesf-core";
import { Button, CircularProgress, Grid2 } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { capitalize } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { updateCarrierStatus } from "../repositories/CarrierAssemblyRepository";

const moment = getMomentTz();

export const ActionModal = ({
	open,
	onHide,
	setNotificationMessage,
	partId,
	partName,
	action = { action: "", code: "" },
	setWarningMessage,
}: {
	open: boolean;
	onHide: () => void;
	setNotificationMessage: (message: string) => void;
	partId: number;
	partName: string;
	action: {
		action?: string | null;
		code?: string | null;
	};
	setWarningMessage: (message: string) => void;
}) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");

	const { control, reset, getValues, setValue } = useForm({
		defaultValues: { timestamp: new Date() },
		mode: "onChange",
	});

	const queryClient = useQueryClient();
	const updateStatus = useMutation(
		({ newStatus }: { newStatus: string }) => {
			setIsSubmitLoading(true);
			return updateCarrierStatus({
				partId: partId,
				newStatus,
				timestamp: getValues("timestamp") ?? null,
			});
		},
		{
			onSuccess: async (warningMessage?: string) => {
				await queryClient.invalidateQueries({ queryKey: ["carriersAssembly"] });
				await queryClient.invalidateQueries({ queryKey: ["carrierInstances"] });
				setNotificationMessage(`The carrier was ${action.action} successfully`);
				setWarningMessage(warningMessage || "");
				onHide();
			},
			onError: (error: Error) => {
				setError(error.message);
			},
			onSettled: () => setIsSubmitLoading(false),
		},
	);

	useEffect(() => {
		reset();
	}, [open]);

	const validateNotInFuture = (description: string, value: Date | null) => {
		if (value && value > new Date()) {
			return `${description} time cannot be in the future.`;
		}
		return true;
	};

	return (
		<>
			{open && (
				<HelmetDexteel
					title={`${partName} - ${capitalize(action?.action ?? "")} Carrier`}
				/>
			)}
			<MesfModal
				title={`CONFIRM ${action.action?.toUpperCase()}`}
				open={open}
				id="confirm-action"
				maxWidth="sm"
				handleClose={() => onHide()}
			>
				<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
					<Grid2 container spacing={2}>
						<Grid2 container size={{ xs: 12, md: 12 }} spacing={1}>
							<Grid2 style={{ display: "flex", gap: 10 }} size={{ md: 12 }}>
								<Controller
									name="timestamp"
									control={control}
									shouldUnregister
									rules={{
										validate: {
											notInFuture: (value) =>
												validateNotInFuture("timestamp", value),
										},
									}}
									render={({ field, fieldState: { error } }) => (
										<DateTimePicker
											label="Timestamp"
											value={field.value ? moment(field.value) : null}
											onChange={field.onChange}
											timezone={TimeService.getInstance().getServerTimeZone()}
											format="MM/DD/YYYY HH:mm:ss"
											ampm={false}
											slotProps={{
												textField: {
													size: "small",
													fullWidth: true,
													required: true,
													helperText: error?.message,
													error: !!error,
												},
											}}
										/>
									)}
								/>
								<Button
									variant="contained"
									color="primary"
									size="small"
									onClick={() => setValue("timestamp", new Date())}
									data-test-id="date-now-button"
								>
									NOW
								</Button>
							</Grid2>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => {
									onHide();
								}}
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								onClick={() => {
									updateStatus.mutate({ newStatus: action.code! });
								}}
								startIcon={
									isSubmitLoading ? (
										<CircularProgress size="1rem" color="inherit" />
									) : null
								}
								variant="contained"
								color="primary"
								disabled={!getValues("timestamp") || isSubmitLoading}
								data-test-id={`confirm-${action.action?.toLowerCase()}-button`}
							>
								Yes
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
